import { TvvBesteldeRoute, TvvStandaardRoute, useGetTvvStandaardRoutes } from "@/entities/tvv";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { BestelTVVRoutesContext, TvvMode } from "./bestel-routes-context";

type BestelRoutesProviderProps = {
    children: ReactNode;
    dossierId: string;
    besteldeRoutes: TvvBesteldeRoute[];
};

export function BestelRoutesProvider({ children, besteldeRoutes, dossierId }: Readonly<BestelRoutesProviderProps>) {
    const { data: routes } = useGetTvvStandaardRoutes(dossierId);
    const [mode, setMode] = useState<TvvMode>(besteldeRoutes.length ? "overzicht" : "selecteren");
    const [selectedRouteIds, setSelectedRouteIds] = useState<number[]>([]);
    const [manuallyAddedRoutes, setManuallyAddedRoutes] = useState<TvvStandaardRoute[]>([]);
    const [replacedRouteIds, setReplacedRouteIds] = useState<Set<number>>(new Set());
    const availableRoutes = useMemo(() => {
        const manualIds = new Set(manuallyAddedRoutes.map((r) => r.routeId));
        const fetchedRoutes = (routes ?? []).filter((r) => !manualIds.has(r.routeId) && !replacedRouteIds.has(r.routeId));
        return [...manuallyAddedRoutes, ...fetchedRoutes];
    }, [manuallyAddedRoutes, routes, replacedRouteIds]);

    const selectedRoutes = useMemo(
        () =>
            selectedRouteIds
                .map((routeId) => availableRoutes.find((route) => route.routeId === routeId))
                .filter((route): route is TvvStandaardRoute => Boolean(route)),
        [availableRoutes, selectedRouteIds]
    );

    const toggleSelectedRoute = useCallback((routeId: number) => {
        setSelectedRouteIds((prev) => (prev.includes(routeId) ? prev.filter((id) => id !== routeId) : [...prev, routeId]));
    }, []);

    const makeRouteAvailable = useCallback((route: TvvStandaardRoute, select: boolean, replaceId?: number) => {
        setManuallyAddedRoutes((prev) => {
            const withoutReplaced = replaceId != null ? prev.filter((r) => r.routeId !== replaceId) : prev;
            if (withoutReplaced.some((r) => r.routeId === route.routeId)) return withoutReplaced;
            return [...withoutReplaced, route];
        });
        setSelectedRouteIds((prev) => {
            if (replaceId == null) {
                return select ? [...prev, route.routeId] : prev;
            }
            return select ? prev.map((id) => (id === replaceId ? route.routeId : id)) : prev.filter((id) => id !== replaceId);
        });
        if (replaceId != null) {
            setReplacedRouteIds((prev) => new Set([...prev, replaceId]));
        }
    }, []);

    const isSelected = useCallback((routeId: number): boolean => selectedRouteIds.includes(routeId), [selectedRouteIds]);
    const isAlreadyBesteld = useCallback(
        (routeId: number) => !!besteldeRoutes.find((route) => route.standaardRoute?.routeId === routeId),
        [besteldeRoutes]
    );

    const isAlreadyAdded = useCallback(
        (routeId: number): boolean => {
            const isBesteld = isAlreadyBesteld(routeId);
            const isAlreadyAvailable = !!availableRoutes.find((route) => route.routeId === routeId);
            return isBesteld || isAlreadyAvailable;
        },
        [isAlreadyBesteld, availableRoutes]
    );

    const clearSelectedRoutes = useCallback(() => {
        setSelectedRouteIds([]);
        setManuallyAddedRoutes([]);
        setReplacedRouteIds(new Set());
    }, []);

    const context = useMemo(
        () => ({
            mode,
            setMode,
            besteldeRoutes,
            availableRoutes,
            selectedRoutes,

            toggleSelectedRoute,
            makeRouteAvailable,
            clearSelectedRoutes,

            isSelected,
            isAlreadyAdded,
            isAlreadyBesteld,
        }),
        [
            availableRoutes,
            besteldeRoutes,
            clearSelectedRoutes,
            isAlreadyAdded,
            isAlreadyBesteld,
            isSelected,
            makeRouteAvailable,
            mode,
            selectedRoutes,
            toggleSelectedRoute,
        ]
    );

    return <BestelTVVRoutesContext value={context}>{children}</BestelTVVRoutesContext>;
}
